const state = {
  bluetoothDevice: null,
  server: null,
  characteristic: null,
  isConnecting: false,
  error: null,
  isInitialized: false
};

const mutations = {
  SET_DEVICE(state, device) {
    state.bluetoothDevice = device;
  },
  SET_SERVER(state, server) {
    state.server = server;
  },
  SET_CHARACTERISTIC(state, characteristic) {
    state.characteristic = characteristic;
  },
  SET_CONNECTING(state, isConnecting) {
    state.isConnecting = isConnecting;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_INITIALIZED(state, value) {
    state.isInitialized = value;
  },
  RESET_STATE(state) {
    state.bluetoothDevice = null;
    state.server = null;
    state.characteristic = null;
    state.isConnecting = false;
    state.error = null;
  }
};

const actions = {
  async initializeBluetooth({ commit, dispatch }) {
    if (state.isInitialized || !navigator.bluetooth) return;
    
    commit('SET_INITIALIZED', true);
    
    // Try to reconnect to a previously connected device
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (devices.length > 0) {
        console.log('Found previously connected Bluetooth devices, attempting to reconnect...');
        // Try to reconnect to the first available device
        await dispatch('reconnectToDevice', devices[0]);
      }
    } catch (error) {
      console.warn('Error initializing Bluetooth:', error);
    }
  },
  
  async reconnectToDevice({ commit, dispatch }, device) {
    if (!device || !device.gatt) {
      console.error('Perangkat Bluetooth tidak valid atau tidak memiliki GATT');
      return false;
    }
    
    commit('SET_CONNECTING', true);
    commit('SET_ERROR', null);
    // Add event listener for disconnection
    const handleDisconnect = () => {
      console.log(`Perangkat terputus: ${device.name || 'Tanpa Nama'}`);
      dispatch('handleDisconnection');
    };
    
    try {
      console.log(`Mencoba menyambung ke perangkat: ${device.name || 'Tanpa Nama'}`);
      
      device.addEventListener('gattserverdisconnected', handleDisconnect);
      
      // Connect to the GATT Server
      let server;
      try {
        console.log('Menghubungkan ke server GATT...');
        server = await device.gatt.connect();
      } catch (error) {
        if (error.message.includes('GATT Server is disconnected')) {
          throw new Error('Perangkat tidak dapat dijangkau. Pastikan printer dalam jangkauan dan dinyalakan.');
        } else {
          throw new Error(`Gagal terhubung ke perangkat: ${error.message}`);
        }
      }
      
      // Define all possible service UUIDs for different printer models
      const printerServiceUuids = [
        '00001101-0000-1000-8000-00805f9b34fb',
        '00001101-0000-1000-8000-0002ee000001',
        '000018f0-0000-1000-8000-00805f9b34fb',
        '0000ffe0-0000-1000-8000-00805f9b34fb',
        '0000ff00-0000-1000-8000-00805f9b34fb'
      ];
      
      // Try to find the printer service
      let service = null;
      
      for (const serviceUuid of printerServiceUuids) {
        try {
          console.log(`Mencoba layanan dengan UUID: ${serviceUuid}`);
          service = await server.getPrimaryService(serviceUuid);
          console.log(`Berhasil menemukan layanan dengan UUID: ${serviceUuid}`);
          break;
        } catch (e) {
          console.log(`Layanan tidak ditemukan dengan UUID: ${serviceUuid}`, e);
        }
      }
      
      if (!service) {
        throw new Error('Tidak dapat menemukan layanan printer yang didukung. Pastikan perangkat adalah printer Bluetooth yang kompatibel.');
      }
      
      console.log('Mengambil karakteristik layanan...');
      const characteristics = await service.getCharacteristics();
      const characteristic = characteristics.find(c => 
        c.properties.writeWithoutResponse || c.properties.write
      );
      
      if (!characteristic) {
        throw new Error('Tidak dapat menemukan karakteristik yang dapat ditulis. Perangkat mungkin bukan printer yang didukung.');
      }
      
      // Save everything to state
      commit('SET_DEVICE', device);
      commit('SET_SERVER', server);
      commit('SET_CHARACTERISTIC', characteristic);
      
      console.log(`Berhasil terhubung ke printer: ${device.name || 'Tanpa Nama'}`);
      return true;
      
    } catch (error) {
      console.error('Kesalahan saat menyambung ulang ke perangkat:', error);
      const errorMessage = error.message || 'Gagal menyambung ke perangkat Bluetooth';
      commit('SET_ERROR', { 
        message: errorMessage,
        originalError: error,
        deviceName: device?.name || 'Perangkat Tidak Dikenal'
      });
      
      // Clean up event listener
      if (device) {
        device.removeEventListener('gattserverdisconnected', handleDisconnect);
      }
      
      return false;
    } finally {
      commit('SET_CONNECTING', false);
    }
  },
  
  async connectBluetooth({ commit, dispatch, state }) {
    if (state.bluetoothDevice) return true;
    
    commit('SET_CONNECTING', true);
    commit('SET_ERROR', null);
    
    try {
      if (!navigator.bluetooth) {
        throw new Error('Bluetooth is not supported in your browser.');
      }
      
      // Define all possible service UUIDs for different printer models
      const printerServiceUuids = [
        '00001101-0000-1000-8000-00805f9b34fb',
        '00001101-0000-1000-8000-0002ee000001',
        '000018f0-0000-1000-8000-00805f9b34fb',
        '0000ffe0-0000-1000-8000-00805f9b34fb',
        '0000ff00-0000-1000-8000-00805f9b34fb'
      ];

      // Request Bluetooth device
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: printerServiceUuids,
      });
      
      if (!device || !device.name) {
        throw new Error('Could not connect to the specified Bluetooth device');
      }
      
      // Connect using the same logic as reconnection
      const success = await dispatch('reconnectToDevice', device);
      if (!success) {
        throw new Error('Failed to connect to the device');
      }
      
      return true;
      
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      commit('SET_ERROR', error);
      dispatch('disconnectBluetooth');
      throw error;
    } finally {
      commit('SET_CONNECTING', false);
    }
  },
  
  async disconnectBluetooth({ commit, state }) {
    if (state.server && state.server.connected) {
      try {
        state.server.disconnect();
      } catch (e) {
        console.error('Error disconnecting:', e);
      }
    }
    
    if (state.bluetoothDevice) {
      state.bluetoothDevice.removeEventListener('gattserverdisconnected', state.handleDisconnection);
    }
    
    commit('RESET_STATE');
  },
  
  handleDisconnection({ dispatch }) {
    console.log('Bluetooth device disconnected');
    dispatch('disconnectBluetooth');
  },
  
  async sendToPrinter({ state }, data) {
    if (!state.characteristic) {
      throw new Error('Not connected to a Bluetooth device');
    }
    
    try {
      await state.characteristic.writeValueWithoutResponse(data);
    } catch (error) {
      console.error('Error sending data to printer:', error);
      throw error;
    }
  }
};

const getters = {
  isConnected: state => !!state.bluetoothDevice,
  deviceName: state => state.bluetoothDevice?.name || null,
  isConnecting: state => state.isConnecting,
  connectionError: state => state.error?.message || null,
  isInitialized: state => state.isInitialized
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
